/**
 * Bilder beim Hochladen verkleinern und als JPEG speichern.
 *
 * Zwei Gründe: Ein Foto vom Handy hat 4000 Pixel und 3 MB — mehr, als der
 * Chat zeigt oder das Modell braucht —, und das iPhone liefert HEIC, das
 * Chrome und Android nicht anzeigen. Nach dem Verkleinern liegt überall ein
 * JPEG, das jeder Browser zeigt und das in den Prompt der Kamera-Erkennung
 * passt (lib/referenzbilder.ts im API-Worker).
 */

/** Längste Kante für Showcase-Bilder im Chat: scharf auf jedem Handy, klein genug zum Laden. */
export const BILD_KANTE = 1600
/** Längste Kante für Referenzfotos: Das Modell sieht damit alles, der Prompt bleibt klein. */
export const REFERENZ_KANTE = 768

/** Zielgröße: höchstens `kante` an der längsten Seite, nie vergrößern. */
export function zielMasse(
  breite: number,
  hoehe: number,
  kante: number,
): { breite: number; hoehe: number } {
  const faktor = Math.min(1, kante / Math.max(breite, hoehe, 1))
  return {
    breite: Math.max(1, Math.round(breite * faktor)),
    hoehe: Math.max(1, Math.round(hoehe * faktor)),
  }
}

/** `IMG_8157.HEIC` → `IMG_8157.jpg`; ohne Endung bleibt der Name und bekommt `.jpg`. */
export function alsJpgName(name: string): string {
  const basis = name.replace(/\.[^./\\]+$/, '') || 'bild'
  return `${basis}.jpg`
}

/**
 * Verkleinert `datei` auf höchstens `kante` Pixel und gibt ein JPEG zurück.
 * `null`, wenn der Browser das Format nicht lesen kann (HEIC in Chrome) —
 * dann soll nichts hochgeladen werden, denn Besucher sähen es auch nicht.
 */
export async function verkleinereBild(datei: File, kante: number): Promise<File | null> {
  let bitmap: ImageBitmap | null = null
  try {
    bitmap = await createImageBitmap(datei)
    const { breite, hoehe } = zielMasse(bitmap.width, bitmap.height, kante)
    const canvas = document.createElement('canvas')
    canvas.width = breite
    canvas.height = hoehe
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    // Weiß darunter: Transparente PNGs würden als JPEG sonst schwarz.
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, breite, hoehe)
    ctx.drawImage(bitmap, 0, 0, breite, hoehe)
    const jpeg = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85))
    if (!jpeg) return null
    return new File([jpeg], alsJpgName(datei.name), { type: 'image/jpeg' })
  } catch {
    return null
  } finally {
    // Ein dekodiertes Großbild belegt sonst bis zu 100 MB, bis der Garbage Collector kommt.
    bitmap?.close()
  }
}

export const BILD_NICHT_LESBAR =
  'Dieses Bildformat kann der Browser nicht lesen (zum Beispiel HEIC vom iPhone in Chrome). Bitte als JPG oder PNG hochladen — am iPhone: Einstellungen → Kamera → Formate → „Maximale Kompatibilität“.'
